import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import mockRnDService from '../../services/mockRnDService';
import { fetchUsersByRoles, formatUserForDropdown } from '../../utils/userUtils';

const ProjectEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [managers, setManagers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    target_date: '',
    priority: 'medium',
    manager: ''
  });

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const data = await mockRnDService.getProject(parseInt(id));
        setFormData({
          name: data.name || '',
          description: data.description || '',
          start_date: data.start_date || '',
          target_date: data.target_date || '',
          priority: data.priority || 'medium',
          manager: data.manager || ''
        });
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Gagal memuat data proyek. ' + err.message);
        toast.error('Gagal memuat data proyek');
      } finally {
        setLoading(false);
      }
    };

    const loadManagers = async () => {
      const roleNames = ['owner', 'admin', 'manager', 'rnd', 'spv rnd', 'rnd manager', 'general manager'];
      const users = await fetchUsersByRoles(roleNames);
      const formattedManagers = users.map(formatUserForDropdown);
      
      if (formattedManagers.length > 0) {
        setManagers(formattedManagers);
      } else {
        setManagers([
          { id: 1, name: 'Muhammad Botee', role: 'Owner' },
          { id: 2, name: 'Fatkhu Rozak', role: 'RnD Manager' },
          { id: 3, name: 'Andi', role: 'RnD Staff' },
          { id: 4, name: 'Budi', role: 'SPV RnD' },
          { id: 5, name: 'Admin', role: 'Admin' }
        ]);
      }
    };

    fetchProject();
    loadManagers();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Find the manager name from the selected ID
      let managerName = formData.manager;
      const selectedManager = managers.find(m => m.id === parseInt(formData.manager));
      if (selectedManager) {
        managerName = selectedManager.name;
      }

      // Update the project with the new data
      await mockRnDService.updateProject(id, {
        ...formData,
        manager: managerName
      });
      
      toast.success('Proyek berhasil diperbarui!');
      navigate(`/rnd/projects/${id}`);
    } catch (err) {
      console.error('Error updating project:', err);
      toast.error('Gagal memperbarui proyek: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="mt-3">Memuat data proyek...</p>
          </div>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container fluid className="py-4">
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Edit Proyek</h4>
              <Button 
                variant="primary" 
                as={Link} 
                to={`/rnd/projects/${id}`}
              >
                <FaArrowLeft className="me-1" /> Kembali ke Detail
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Nama Proyek <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Deskripsi Proyek</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tanggal Mulai <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Target Selesai <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="target_date"
                      value={formData.target_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prioritas</Form.Label>
                    <Form.Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                    >
                      <option value="high">Tinggi</option>
                      <option value="medium">Menengah</option>
                      <option value="low">Rendah</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Manager</Form.Label>
                    <Form.Select
                      name="manager"
                      value={formData.manager}
                      onChange={handleChange}
                    >
                      <option value="">-- Pilih Manager --</option>
                      {managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} ({manager.role})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="mt-4 d-flex justify-content-end">
                <Button 
                  variant="secondary" 
                  className="me-2" 
                  as={Link} 
                  to={`/rnd/projects/${id}`}
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  variant="success" 
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" /> Simpan Perubahan
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default ProjectEditPage;